import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    inject,
    Injector,
    Input,
    OnInit,
    ViewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonicModule, IonInfiniteScroll, Platform } from '@ionic/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TuiSheetOptions, TuiSheetService } from '@taiga-ui/addon-mobile';
import { TuiAutoFocusModule, tuiIsPresent, TuiLetModule } from '@taiga-ui/cdk';
import {
    TuiButtonModule,
    TuiGroupModule,
    TuiLoaderModule,
    TuiModeModule,
    TuiSvgModule,
    TuiTextfieldControllerModule
} from '@taiga-ui/core';
import { TuiAvatarModule, TuiCarouselModule, TuiInputModule, TuiIslandModule } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CategoriesStore } from '@us/web/categories/data-access';
import { Category, CategoryWithoutItems, Food, ProductLayout, ResponsiveStore } from '@us/web/shared/data-access';
import { selectCategoryScrollList, selectProductLayout, selectShop } from '@us/web/shop/data-access';
import { findLast } from 'lodash';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    Observable,
    startWith,
    Subscription,
    switchMap,
    tap,
    timer
} from 'rxjs';
import { SelectCategoryComponent } from '../../components/select-category/select-category.component';
import { ProductItemComponent } from '../product-item/product-item.component';
import {ActivatedRoute} from "@angular/router";

@UntilDestroy()
@Component({
    selector: 'us-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        ReactiveFormsModule,
        TranslocoModule,
        TuiLetModule,
        AsyncPipe,
        NgForOf,
        NgClass,
        ProductItemComponent,
        IonicModule,
        TuiInputModule,
        TuiTextfieldControllerModule,
        TuiGroupModule,
        TuiButtonModule,
        TuiSvgModule,
        TuiAutoFocusModule,
        TuiLoaderModule,
        TuiCarouselModule,
        TuiModeModule,
        TuiIslandModule,
        TuiAvatarModule
    ],
    standalone: true,
    providers: []
})
export class ProductsComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly categoriesStore = inject(CategoriesStore);
    private readonly sheetService = inject(TuiSheetService);
    private readonly injector = inject(Injector);
    private readonly translocoService = inject(TranslocoService);
    private readonly router = inject(ActivatedRoute);

    @Input() showHeader = true;

    readonly selectedCategory$ = this.categoriesStore.selectedCategory$;
    readonly searchInputFocused$ = this.categoriesStore.searchInputFocused$;
    readonly filteredCategories$: Observable<Category[]> = this.categoriesStore.filteredCategories$;
    readonly isLoading$: Observable<boolean> = this.categoriesStore.isLoading$;

    readonly layout$: Observable<ProductLayout> = this.store.select(selectProductLayout);
    readonly isScrollList$ = this.store.select(selectCategoryScrollList);
    readonly categories$ = this.categoriesStore.categories$;
    readonly DEFAULT_IMAGE = '/assets/icons/picture.svg';
    public platform = inject(Platform);
    private readonly responsiveStore = inject(ResponsiveStore);
    public readonly index_count$ = this.responsiveStore.indexCount$;
    public readonly width_category$ = this.responsiveStore.widthCategory$;
    readonly shop = this.store.selectSignal(selectShop);

    readonly searchCtrl = new FormControl<string>('');

    readonly Layout = ProductLayout;
    index = 0;

    // @ViewChild(IonInfiniteScroll) infiniteScroll!: IonInfiniteScroll;

    private isLocked = false;
    private readonly CONTENT_OFFSET = 180;
    private timeoutHandler: Subscription | null = null;
    private selectedCategoryId: number | undefined;

    readonly INPUT_SIZE: 's' | 'm' = 'm';

    @HostListener('window:scroll', ['$event'])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onScroll(event: any) {
        if (event.detail) {
            if (this.isLocked) {
                return;
            }

            this.timeoutHandler?.unsubscribe();

            this.timeoutHandler = timer(500)
                .pipe(
                    switchMap(() => this.filteredCategories$),
                    first(),
                    tap(categories => {
                        const verticalOffset: number = event.detail.currentY;
                        const category = findLast(categories, category => {
                            const element = document.getElementById(`category-${category.id}`) as HTMLElement;
                            return element && verticalOffset + this.CONTENT_OFFSET >= element.offsetTop;
                        });

                        if (category) {
                            this.selectedCategoryId = category.id;
                            this.categoriesStore.selectCategory(category);
                        }
                    })
                )
                .subscribe();
        }
    }

    ngOnInit() {
        this.searchCtrl.valueChanges
            .pipe(
                untilDestroyed(this),
                filter(tuiIsPresent),
                startWith(''),
                debounceTime(300),
                distinctUntilChanged(),
                tap(searchString => {
                    this.categoriesStore.patchState({ searchString });
                })
            )
            .subscribe();

        this.selectedCategory$
            .pipe(
                untilDestroyed(this),
                filter(tuiIsPresent),
                distinctUntilChanged(),
                filter(category => category.id !== this.selectedCategoryId),
                tap(category => {
                    this.selectCategory(category.id);
                })
            )
            .subscribe();


    }

    ngAfterViewInit() {
        console.log('AfterViewInit');
        const pageYOffset = Number(this.router.snapshot.queryParamMap.get('pageYOffset') || 0);
        if(pageYOffset > 0) {
            setTimeout(()=> {
                window.scrollTo({
                    top: pageYOffset,
                    behavior: 'instant' as ScrollBehavior
                });
            })
        }
    }

    trackById(index: number, item: Category | Food): number {
        return item.id;
    }

    // private loadData($event: any = null) {
    //     const offset = this.currentTotalItems;
    //     const size = this.SIZE;
    //     const categories = [...this.filteredCategories];
    //     for (let i = 0; i < size; i++) {
    //         if (this.currentTotalItems + 1 >= this.totalItems) {
    //             if (this.infiniteScroll) {
    //                 this.infiniteScroll.disabled = true;
    //             }
    //             break;
    //         }

    //         const item = this.allItems[offset + i + 1];
    //         const index = categories.findIndex(e => e.id === item.category_id);
    //         if (index !== -1) {
    //             categories[index].items.push(item);
    //         } else {
    //             categories.push({
    //                 id: Number(item.category_id),
    //                 name: item.category_name + '',
    //                 items: [item]
    //             });
    //         }
    //         this.currentTotalItems += 1;
    //     }

    //     this.filteredCategories = [...categories];
    //     this.changeDetectorRef.detectChanges();
    //     setTimeout(() => {
    //         $event?.target.complete();
    //     }, 300);
    // }

    private selectCategory(id: number) {
        // while (!this.filteredCategories.some(e => e.id === id) && this.currentTotalItems + 1 < this.totalItems) {
        //     console.log('load more');
        //     this.loadData();
        // }

        this.scrollToCategory(id);
    }

    private scrollToCategory(id: number) {
        const el = document.getElementById(`category-${id}`);

        if (!el) {
            console.error('Element not found');
            return;
        }
        this.isLocked = true;

        el.scrollIntoView({
            behavior: 'smooth'
        } as ScrollIntoViewOptions);

        setTimeout(() => {
            this.isLocked = false;
        }, 600);
    }

    handleClickCategoryBtn(): void {
        this.selectedCategory$
            .pipe(
                first(),
                switchMap(selectedCategory =>
                    this.sheetService.open<{ category: CategoryWithoutItems }>(
                        new PolymorpheusComponent(SelectCategoryComponent, this.injector),
                        {
                            dismissible: true,
                            overlay: true,
                            label: this.translocoService.translate('select_category'),
                            data: {
                                selectedCategory
                            }
                        } as unknown as Partial<TuiSheetOptions>
                    )
                ),
                tap(({ category }) => {
                    this.categoriesStore.selectCategory(category);
                })
            )
            .subscribe();
    }

    handleSearchInputFocus(focused: boolean): void {
        this.categoriesStore.focusSearchInput(focused || this.searchCtrl.value !== '');
    }

    handleCloseSearchInput(): void {
        this.searchCtrl.setValue('');
        this.categoriesStore.focusSearchInput(false);
    }

    handleSelect(category: CategoryWithoutItems): void {
        this.categoriesStore.selectCategory(category);
    }
}
